import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TablePagination from "@mui/material/TablePagination";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { MerchantInboxItem } from "../../domain/merchant";
import { getMerchantInbox } from "../../services/api/merchant";
import { normalizeError } from "../../services/http/errors";

type AnyInboxItem = MerchantInboxItem & {
  createdAt?: string | number;
  updatedAt?: string | number;
  ts?: string | number;
};

const STATUS_FILTERS = ["ALL", "REQUESTED", "APPROVED", "IN_TRANSIT", "RECEIVED", "RESOLVED", "REJECTED", "CANCELLED"] as const;

const statusColor = (s: string): "warning" | "success" | "info" | "error" | "default" => {
  switch (s) {
    case "REQUESTED": return "warning";
    case "APPROVED": return "success";
    case "IN_TRANSIT": return "info";
    case "RECEIVED": return "info";
    case "RESOLVED": return "success";
    case "REJECTED": return "error";
    case "CANCELLED": return "default";
    default: return "default";
  }
};

export default function MerchantInbox() {
  const { tenantSlug = "" } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [items, setItems] = useState<AnyInboxItem[]>([]);
  const [toast, setToast] = useState({ open: false, msg: "" });
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const load = async () => {
    try {
      const data = (await getMerchantInbox(tenantSlug)) as AnyInboxItem[];
      setItems(data || []);
      setPage(0);
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, msg: err.message });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTime = (x: AnyInboxItem) => {
    const v = x.requestedAt ?? x.createdAt ?? x.updatedAt ?? x.ts;
    if (v === undefined || v === null) return NaN;
    if (typeof v === "number") return v;
    const t = Date.parse(String(v));
    return Number.isNaN(t) ? NaN : t;
  };

  const filtered = useMemo(() => {
    let result = items;

    // Status filter
    if (statusFilter !== "ALL") {
      result = result.filter((x) => x.status === statusFilter);
    }

    // Text search
    const s = q.trim().toLowerCase();
    if (s) {
      result = result.filter(
        (x) =>
          x.rma.toLowerCase().includes(s) ||
          x.orderRef.toLowerCase().includes(s) ||
          x.customerEmail.toLowerCase().includes(s)
      );
    }

    return result;
  }, [items, q, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const ta = getTime(a);
      const tb = getTime(b);
      const aHas = !Number.isNaN(ta);
      const bHas = !Number.isNaN(tb);
      if (aHas && bHas) return tb - ta;
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      return String(b.rma).localeCompare(String(a.rma));
    });
    return arr;
  }, [filtered]);

  const pageItems = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const goToDetail = (rma: string) => nav(`/merchant/${tenantSlug}/inbox/${rma}`);

  return (
    <>
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ open: false, msg: "" })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setToast({ open: false, msg: "" })} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      <Card sx={{ width: "100%" }}>
        {/* Toolbar */}
        <Stack sx={{ p: 2, borderBottom: 1, borderColor: "divider", gap: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: "wrap", gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search by RMA, order or customer..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(0); }}
              sx={{ flex: "1 1 300px", minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" size="small" startIcon={<SyncIcon />} onClick={load}>
              Refresh
            </Button>
          </Stack>

          {/* Status filter tabs */}
          <Box sx={{ overflowX: "auto" }}>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, val) => { if (val !== null) { setStatusFilter(val); setPage(0); } }}
              size="small"
              sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, py: 0.5, fontSize: 13, borderRadius: "8px !important" } }}
            >
              {STATUS_FILTERS.map((s) => (
                <ToggleButton key={s} value={s}>
                  {s === "ALL" ? "All" : s === "IN_TRANSIT" ? "In Transit" : s.charAt(0) + s.slice(1).toLowerCase()}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>

        {/* Desktop Table */}
        <Box sx={{ overflowX: "auto", display: { xs: "none", md: "block" } }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>RMA Identity</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Resolution</TableCell>
                <TableCell align="right">Value (SAR)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((r) => (
                <TableRow key={r.rma} hover onClick={() => goToDetail(r.rma)} sx={{ cursor: "pointer" }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{r.rma}</Typography>
                    <Typography variant="caption" color="text.secondary">ORDER {r.orderRef}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: "primary.light", color: "primary.main" }}>
                        {r.customerEmail.substring(0, 2).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{r.customerEmail}</Typography>
                        <Typography variant="caption" color="primary.main" fontWeight={600}>Verified</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.resolution}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {r.totalRequestValue != null ? r.totalRequestValue.toFixed(2) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status.replace(/_/g, " ")} size="small" color={statusColor(r.status)} variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
              {pageItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {statusFilter !== "ALL" ? `No ${statusFilter.toLowerCase()} returns` : "No return requests yet"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Mobile Cards */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {pageItems.map((r) => (
            <CardContent
              key={`m-${r.rma}`}
              onClick={() => goToDetail(r.rma)}
              sx={{ borderBottom: 1, borderColor: "divider", cursor: "pointer", "&:hover": { bgcolor: "grey.50" } }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>{r.rma}</Typography>
                  <Typography variant="caption" color="text.secondary">ORDER {r.orderRef}</Typography>
                </Box>
                <Chip label={r.status.replace(/_/g, " ")} size="small" color={statusColor(r.status)} variant="outlined" />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: 1, borderColor: "divider", flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10 }}>Customer</Typography>
                  <Typography variant="body2" fontWeight={600} noWrap>{r.customerEmail}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: 1, borderColor: "divider", flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10 }}>Value</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {r.totalRequestValue != null ? `SAR ${r.totalRequestValue.toFixed(2)}` : "—"}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          ))}
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
        />
      </Card>
    </>
  );
}
